import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  CreditCard,
  Check,
  HelpCircle,
  ArrowRight,
  Zap,
  Rocket,
  TrendingUp,
  ShieldCheck,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { Button, FeatureIcon } from '@/components/ui';
import { Card } from '@/components/ui/Card';
import { SectionDivider } from '@/components/decorative';

interface PricingTier {
  name: string;
  price: string;
  priceNote: string;
  description: string;
  icon: React.ReactNode;
  gradient: string;
  features: string[];
  details: string[];
  highlighted?: boolean;
  cta: string;
  ctaVariant: 'primary' | 'outline';
}

const tiers: PricingTier[] = [
  {
    name: 'Free',
    price: '0',
    priceNote: 'to evaluate',
    description: 'Try it on your own content.',
    icon: <Zap className="w-5 h-5 text-slate-400" />,
    gradient: 'from-slate-500/20 to-gray-500/20',
    features: ['1 service', '1 channel', 'Up to 50 conversations'],
    details: ['500K tokens / month', 'Knowledge base included', 'No card required', 'GDPR compliant'],
    cta: 'Get Started',
    ctaVariant: 'outline',
  },
  {
    name: 'Starter',
    price: '39',
    priceNote: 'per month',
    description: 'Small teams getting going.',
    icon: <Rocket className="w-5 h-5 text-emerald-400" />,
    gradient: 'from-emerald-500/20 to-teal-500/20',
    features: ['1 service', '1 channel', '250 conversations / month'],
    details: ['5M input / 250K output tokens', 'All integrations', 'Email support', 'GDPR compliant'],
    cta: 'Get Started',
    ctaVariant: 'outline',
  },
  {
    name: 'Scale',
    price: '299',
    priceNote: 'per month',
    description: 'Growing volume, all channels.',
    icon: <TrendingUp className="w-5 h-5 text-indigo-400" />,
    gradient: 'from-indigo-500/20 to-purple-500/20',
    features: ['10 services', 'All channels', '3,500 conversations / month'],
    details: ['25M input / 1.25M output tokens', 'Harness pack per service type, included', 'MCP endpoint', 'Bring your own model', 'Priority support', 'Dedicated inference available', 'GDPR compliant'],
    highlighted: true,
    cta: 'Get Started',
    ctaVariant: 'primary',
  },
  {
    name: 'Custom',
    price: 'Custom',
    priceNote: 'Talk to us',
    description: 'Contracted volume, or your own infrastructure.',
    icon: <ShieldCheck className="w-5 h-5 text-cyan-400" />,
    gradient: 'from-cyan-500/20 to-blue-500/20',
    features: ['Unlimited services', 'Contracted conversation volume', 'Dedicated or partner inference'],
    details: ['Unlimited tokens', 'Harness packs, including custom verticals', 'Support with response objectives', 'Private deployment of our own stack, Fnk', 'GDPR compliant'],
    cta: 'See Enterprise',
    ctaVariant: 'outline',
  },
];

const faqs = [
  { question: 'What counts as a conversation?', answer: 'A conversation is one customer session your assistant handles — however many messages it takes. Sessions your team takes over manually do not count against your plan.' },
  { question: 'What are service types?', answer: 'Your business category — each comes with tailored integrations.' },
  { question: 'Is there a free tier?', answer: 'Yes. Free gives you one service on one channel and enough conversations to evaluate the product on your own content. No card required.' },
  { question: 'When does pricing take effect?', answer: 'All plans are free during Open Beta. Prices shown will apply after general availability. Early adopters will receive favorable terms.' },
  { question: 'What happens if I go over my plan?', answer: 'Your service stays online. Move up a plan at any time — we will tell you before anything changes on your bill, and never cut a service off mid-conversation.' },
  { question: 'What are the token limits for?', answer: 'Each plan carries a monthly token allowance as a fair-use ceiling — Free 500K, Starter 5M input and 250K output, Scale 25M input and 1.25M output, unlimited on Custom. Normal conversational use stays well inside them; they exist so one content-heavy service cannot starve the rest.' },
  { question: 'Which model runs my service?', answer: 'An open-weight model on our own AI stack, Fnk, rather than reselling a model vendor\u2019s API. That is what lets us price per conversation and keep the margin as volume grows. On Scale you can also connect your own approved model endpoint.' },
  { question: 'What is the Harness, and do I pay extra for it?', answer: 'The Harness is the configured layer that makes your service work out of the box \u2014 your vertical\u2019s workflows, the tool and action-card schemas, the prompt and policy packs, the confirmation gates, the grounding configuration and the evaluation sets that rerun whenever the model changes. It is included in every plan at no extra charge, configured at onboarding.' },
  { question: 'Can I use the Harness with my own model or agents?', answer: 'Yes. A Harness pack names a policy, never a provider, so it runs on our inference, on an open-weight model, on any OpenAI-compatible endpoint you already pay for, or against an agent runtime you already operate. The Harness is part of our own stack, Fnk, and ships inside every DialogMe plan.' },
  { question: 'Can I run this on my own infrastructure?', answer: 'Yes, as a Fnk deployment. DialogMe is a managed platform, and our own stack, Fnk, installs inside your environment \u2014 private cloud, on-premises or air-gapped. Your service types and Harness packs carry over. See the Enterprise page.' },
];

function PricingCard({ tier, expanded, onToggle }: { tier: PricingTier; expanded: boolean; onToggle: () => void }) {
  const isCustom = tier.price === 'Custom';
  const isFree = tier.price === '0';

  return (
    <div
      className={`relative rounded-2xl flex flex-col h-full transition-all duration-300 hover:-translate-y-1 ${
        tier.highlighted
          ? 'bg-gradient-to-b from-indigo-500/[0.12] to-slate-900/80 border border-indigo-500/30 shadow-[0_0_40px_-12px_rgba(99,102,241,0.25)]'
          : 'bg-slate-800/40 border border-slate-700/40 shadow-lg shadow-black/10 hover:border-slate-600/60'
      }`}
    >
      <div className="p-6 flex flex-col h-full">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <FeatureIcon gradient={tier.gradient} size="sm">
            {tier.icon}
          </FeatureIcon>
          <div>
            <h3 className="text-sm font-semibold text-white tracking-tight">{tier.name}</h3>
            <p className="text-[11px] text-slate-500 min-h-[2rem]">{tier.description}</p>
          </div>
        </div>

        {/* Price */}
        <div className="pb-5 mb-5 border-b border-slate-700/50">
          {isCustom ? (
            <div>
              <span className="text-3xl font-extrabold text-white">Custom</span>
              <p className="text-xs text-slate-500 mt-1">{tier.priceNote}</p>
            </div>
          ) : isFree ? (
            <div>
              <div className="flex items-baseline gap-0.5">
                <span className="text-3xl font-extrabold text-white">&euro;{tier.price}</span>
              </div>
              <p className="text-xs text-slate-500 mt-1">{tier.priceNote}</p>
            </div>
          ) : (
            <div>
              <div className="flex items-baseline gap-2">
                <span className="text-3xl font-extrabold text-slate-500 line-through decoration-slate-600">&euro;{tier.price}</span>
                <span className="text-lg font-bold text-emerald-400">Free</span>
              </div>
              <p className="text-xs text-amber-400/70 mt-1">Open Beta — free until GA</p>
            </div>
          )}
        </div>

        {/* Features */}
        <div className="flex-1 mb-6">
          <div className="space-y-2.5">
            {tier.features.map((f, i) => (
              <div key={i} className="flex items-start gap-2 text-[13px] text-slate-300">
                <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                <span>{f}</span>
              </div>
            ))}
          </div>

          {/* Expandable details */}
          <div
            className={`overflow-hidden transition-all duration-300 ease-in-out ${
              expanded ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
            }`}
          >
            <div className="space-y-2.5 mt-2.5">
              {tier.details.map((d, i) => (
                <div key={i} className="flex items-start gap-2 text-[13px] text-slate-300">
                  <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{d}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Toggle button */}
          <button
            onClick={onToggle}
            className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-300 transition-colors mt-3"
          >
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            {expanded ? 'Less' : 'See details'}
          </button>
        </div>

        {/* CTA */}
        {isCustom ? (
          <Link to="/enterprise" className="block">
            <Button
              variant={tier.ctaVariant}
              size="md"
              className="w-full justify-center"
              rightIcon={<ArrowRight className="w-4 h-4" />}
            >
              {tier.cta}
            </Button>
          </Link>
        ) : (
          <a
            href="https://console.dialogme.ai"
            target="_blank"
            rel="noopener noreferrer"
            className="block"
          >
            <Button
              variant={tier.ctaVariant}
              size="md"
              className="w-full justify-center"
              rightIcon={<ArrowRight className="w-4 h-4" />}
            >
              {tier.cta}
            </Button>
          </a>
        )}
      </div>
    </div>
  );
}

export function PricingPage() {
  const [expanded, setExpanded] = useState(false);

  return (
    <main className="flex-1 overflow-y-auto">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-20 pb-10 md:py-20">
        {/* Header */}
        <div className="text-center mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-500/10 rounded-full mb-6 border border-indigo-500/20">
            <CreditCard className="w-5 h-5 text-indigo-400" />
            <span className="text-sm font-medium text-indigo-300">Pricing</span>
          </div>
          <h1 className="text-hero-mobile md:text-hero-desktop font-extrabold tracking-heading mb-4 bg-clip-text text-transparent bg-gradient-to-r from-white to-slate-400">
            Start Free. Scale When Ready.
          </h1>
          <p className="text-base sm:text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed">
            Every plan is free during Open Beta. Prices shown apply after general availability.
          </p>
          <div className="flex flex-wrap justify-center gap-3 mt-5">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-sm text-emerald-300">
              <Zap className="w-4 h-4" />
              Open Beta — All Free
            </div>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-sm text-emerald-300">
              <ShieldCheck className="w-4 h-4" />
              GDPR Compliant
            </div>
          </div>
        </div>

        {/* Tier Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 mb-16 md:mb-20">
          {tiers.map((tier) => (
            <PricingCard key={tier.name} tier={tier} expanded={expanded} onToggle={() => setExpanded(e => !e)} />
          ))}
        </div>

        <SectionDivider variant="gradient" />

        {/* FAQ */}
        <div className="my-16 md:my-20">
          <h2 className="text-section-mobile md:text-section-desktop font-bold tracking-subheading text-white mb-8 flex items-center justify-center gap-2">
            <HelpCircle className="w-6 h-6 text-indigo-400" /> FAQ
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {faqs.map((faq, idx) => (
              <Card key={idx} variant="default" hoverable className="p-5">
                <h3 className="text-sm font-semibold text-slate-200 tracking-tight mb-1.5">{faq.question}</h3>
                <p className="text-xs text-slate-400 leading-relaxed">{faq.answer}</p>
              </Card>
            ))}
          </div>
        </div>

        <SectionDivider variant="gradient" />

        {/* CTA */}
        <div className="my-16 md:my-20">
          <Card variant="cta" className="text-center p-6 sm:p-8 md:p-10">
            <h2 className="text-section-mobile md:text-section-desktop font-bold tracking-subheading text-white mb-3">
              Start Building Today
            </h2>
            <p className="text-slate-300 mb-6 leading-relaxed">
              No credit card. No commitment. Cancel anytime.
            </p>
            <a href="https://console.dialogme.ai" target="_blank" rel="noopener noreferrer">
              <Button variant="primary" size="lg" rightIcon={<ArrowRight className="w-4 h-4" />}>
                Get Started
              </Button>
            </a>
          </Card>
        </div>
      </div>
    </main>
  );
}
